import React from "react";
import HoverChat from "../../components/HoverChat";

/**
 * Step 1 — Your energy situation.
 *
 * Inputs: tariff (₹/kWh), diesel hours/day, diesel cost/litre, location.
 * Kept intentionally simple — the ARD's full 30-field studio is not the goal
 * here; the goal is a friendly 3-step flow that ends in an RFP.
 */
const STATES = [
  { code: "TN", label: "Tamil Nadu" },
  { code: "KA", label: "Karnataka" },
  { code: "MH", label: "Maharashtra" },
  { code: "GJ", label: "Gujarat" },
  { code: "AP", label: "Andhra Pradesh" },
  { code: "TS", label: "Telangana" },
  { code: "KL", label: "Kerala" },
  { code: "OTHER", label: "Other" },
];

export default function StepEnergy({ values, onChange, onNext }) {
  const setVal = (patch) => onChange({ ...values, ...patch });

  const canProceed = values.tariff > 0;

  return (
    <div data-testid="audit-step-energy">
      <div className="gg-eyebrow" style={{ marginBottom: 10 }}>
        Step 1 of 3
      </div>
      <h2 className="gg-h1" style={{ maxWidth: "24ch" }}>
        Your energy situation today.
      </h2>
      <p style={{ color: "var(--text-dim)", fontSize: 15.5, lineHeight: 1.7, maxWidth: 640, marginTop: 14 }}>
        Four honest numbers. Any figures you don't know, leave at the sensible
        default — you can refine everything before you submit.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 20,
          marginTop: 32,
          maxWidth: 780,
        }}
      >
        <Field
          label="Grid tariff (₹/kWh)"
          hint={
            <>
              Typical <HoverChat term="lfp">industrial</HoverChat> slab is
              ₹8–12/kWh.
            </>
          }
        >
          <input
            type="number"
            min={0}
            step={0.1}
            value={values.tariff}
            onChange={(e) => setVal({ tariff: parseFloat(e.target.value) || 0 })}
            data-testid="audit-input-tariff"
            style={inputStyle}
          />
        </Field>

        <Field label="Diesel generator hours per day" hint="0 if you don't run a DG.">
          <input
            type="number"
            min={0}
            max={24}
            step={0.5}
            value={values.dgHours}
            onChange={(e) => setVal({ dgHours: parseFloat(e.target.value) || 0 })}
            data-testid="audit-input-dg-hours"
            style={inputStyle}
          />
        </Field>

        <Field label="Diesel cost (₹/litre)" hint="Current market ~₹90–100.">
          <input
            type="number"
            min={0}
            step={0.5}
            value={values.dgCostPerLitre}
            onChange={(e) =>
              setVal({ dgCostPerLitre: parseFloat(e.target.value) || 0 })
            }
            data-testid="audit-input-dg-cost"
            style={inputStyle}
          />
        </Field>

        <Field label="Location">
          <select
            value={values.location}
            onChange={(e) => setVal({ location: e.target.value })}
            data-testid="audit-input-location"
            style={inputStyle}
          >
            {STATES.map((s) => (
              <option key={s.code} value={s.code} style={{ background: "var(--night-2)" }}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Pincode (optional)">
          <input
            type="text"
            maxLength={6}
            value={values.pincode}
            onChange={(e) =>
              setVal({ pincode: e.target.value.replace(/[^0-9]/g, "") })
            }
            data-testid="audit-input-pincode"
            style={inputStyle}
          />
        </Field>
      </div>

      <div style={{ marginTop: 40, display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          data-testid="audit-next-1"
          style={primaryButtonStyle(!canProceed)}
        >
          Continue — your site →
        </button>
      </div>
    </div>
  );
}

// ---- helpers reused across all three steps ----
export const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 15,
  color: "var(--paper)",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid var(--line)",
  borderRadius: 10,
  outline: "none",
  fontFamily: "inherit",
};

export const primaryButtonStyle = (disabled) => ({
  padding: "12px 22px",
  fontSize: 14.5,
  fontWeight: 600,
  fontFamily: "inherit",
  color: disabled ? "var(--text-mute)" : "var(--night)",
  background: disabled
    ? "rgba(255,255,255,0.04)"
    : "linear-gradient(135deg, var(--gold), var(--gold-deep))",
  border: "1px solid",
  borderColor: disabled ? "var(--line)" : "transparent",
  borderRadius: 999,
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "transform 200ms, box-shadow 200ms",
  boxShadow: disabled ? "none" : "0 8px 24px -12px rgba(244,169,31,0.5)",
});

export function Field({ label, hint, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span
        style={{
          fontSize: 11.5,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--text-dim)",
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
      {hint && (
        <span style={{ fontSize: 12, color: "var(--text-mute)", lineHeight: 1.5 }}>
          {hint}
        </span>
      )}
    </label>
  );
}
